'use client';

import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import { KurtosisResult } from '../../../lib/stats/kurtosis';

interface KurtosisCalculatorProps {
  visualizationData: KurtosisResult | null;
}

export function KurtosisCalculator({ visualizationData: result }: KurtosisCalculatorProps) {
  if (!result) {
    return null;
  }

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-2">Kurtosis Results</h3>
          <div className="space-y-4">
            <div>
              <Label>Sample Kurtosis (G2)</Label>
              <div className="text-lg font-semibold">{result.sampleKurtosis.toFixed(4)}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Reference: Normal distribution has kurtosis = 3
              </div>
            </div>
            <div>
              <Label>Sample Excess Kurtosis</Label>
              <div className="text-lg font-semibold">{result.sampleExcessKurtosis.toFixed(4)}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Reference: Normal distribution has excess kurtosis = 0
              </div>
            </div>
            <div>
              <Label>Population Kurtosis</Label>
              <div className="text-lg font-semibold">{result.populationKurtosis.toFixed(4)}</div>
            </div>
            <div>
              <Label>Population Excess Kurtosis</Label>
              <div className="text-lg font-semibold">{result.populationExcessKurtosis.toFixed(4)}</div>
            </div>
            <div>
              <Label>Pearson's Kurtosis (b2)</Label>
              <div className="text-lg font-semibold">{result.pearsonKurtosis.toFixed(4)}</div>
            </div>
            <div>
              <Label>Pearson's Excess Kurtosis</Label>
              <div className="text-lg font-semibold">{result.pearsonExcessKurtosis.toFixed(4)}</div>
            </div>
            <div>
              <Label>Distribution Type</Label>
              <div className="text-lg">{result.type}</div>
            </div>
            <div>
              <Label>Interpretation</Label>
              <div className="text-lg">{result.interpretation}</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Plot</h3>
          <DistributionGraph
            data={result.data}
            height={300}
            showMean
            showMedian
            grid={{
              left: '10%',
              right: '5%',
              top: '10%',
              bottom: '10%'
            }}
          />
        </Card>
      </div>
      <div className="text-sm text-muted-foreground my-4">
      <span>Kurtosis only measures the tailedness of a distribution and not the "peakedness". See {' '}
            <a 
              href="https://www.tandfonline.com/doi/abs/10.1080/00031305.2014.917055"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
               "Kurtosis as Peakedness" (Westfall, 2014)
            </a></span>
          </div>
    </ScrollableContainer>
  );
} 